/**
 * Medical Dashboard Theme Configuration
 * Based on Lucena Health branding with clinical color palette
 */

export const medicalTheme = {
  // Primary Colors from Lucena Health Logo
  colors: {
    primary: {
      teal: '#4A90A4',      // Main brand color from logo
      tealLight: '#5FA9BE',
      tealDark: '#3A7285',
    },
    
    // Risk Level Colors (Clinical)
    risk: {
      low: '#10B981',       // Green - healthy
      medium: '#F59E0B',    // Amber - caution
      high: '#EF4444',      // Red - danger
      critical: '#DC2626',  // Dark red - emergency
      unknown: '#6B7280',   // Gray - no data
    },
    
    // Alert Severity Colors
    severity: {
      low: '#3B82F6',       // Blue - informational
      medium: '#F59E0B',    // Amber - warning
      high: '#EF4444',      // Red - urgent
      critical: '#DC2626',  // Dark red - emergency
    },
    
    // Vital Signs Status
    vitals: {
      normal: '#10B981',
      elevated: '#F59E0B',
      high: '#EF4444',
      low: '#3B82F6',
      critical: '#DC2626',
    },
    
    // UI Colors
    background: {
      primary: '#FFFFFF',
      secondary: '#F9FAFB',
      tertiary: '#F3F4F6',
    },
    
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      tertiary: '#9CA3AF',
      white: '#FFFFFF',
    },
    
    border: {
      light: '#E5E7EB',
      medium: '#D1D5DB',
      dark: '#9CA3AF',
    },
    
    // Adherence Status
    adherence: {
      excellent: '#10B981',  // ≥90%
      good: '#3B82F6',       // 70-89%
      fair: '#F59E0B',       // 50-69%
      poor: '#EF4444',       // <50%
    },
  },
  
  // Typography
  typography: {
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", monospace',
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  
  // Spacing (follows 4px base grid)
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
  },
  
  // Border Radius
  borderRadius: {
    sm: '0.25rem',   // 4px
    md: '0.5rem',    // 8px
    lg: '0.75rem',   // 12px
    xl: '1rem',      // 16px
    full: '9999px',
  },
  
  // Shadows
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
};

/**
 * Get risk level color
 */
export const getRiskColor = (riskLevel) => {
  return medicalTheme.colors.risk[riskLevel?.toLowerCase()] || medicalTheme.colors.risk.unknown;
};

/**
 * Get severity color
 */
export const getSeverityColor = (severity) => {
  return medicalTheme.colors.severity[severity?.toLowerCase()] || medicalTheme.colors.text.secondary;
};

/**
 * Get adherence status and color
 */
export const getAdherenceStatus = (score) => {
  if (score >= 90) return { status: 'excellent', color: medicalTheme.colors.adherence.excellent, label: 'Excellent' };
  if (score >= 70) return { status: 'good', color: medicalTheme.colors.adherence.good, label: 'Good' };
  if (score >= 50) return { status: 'fair', color: medicalTheme.colors.adherence.fair, label: 'Fair' };
  return { status: 'poor', color: medicalTheme.colors.adherence.poor, label: 'Poor' };
};

export default medicalTheme;